import type { TGuidedExperienceProperties } from '@cascade/api-client/ge-v2';
import { skipToken, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ExperienceListContext } from './ExperienceList.context.ts';
import type { TExperienceListProviderProps } from './ExperienceList.types.ts';
import { useApi } from '#providers/api/index.ts';
import { useProjects } from '#providers/projects/index.ts';
import { createQueryKey } from '#providers/query/index.ts';

const noExperiences: TGuidedExperienceProperties[] = [];

export const ExperienceListProvider = ({
  children,
}: TExperienceListProviderProps) => {
  const api = useApi();
  const { currentProjectId, tagValues, isFeatureEnabled } = useProjects();

  const isExperiencesEnabled = isFeatureEnabled('enable_ge');

  const queryKey = [
    ...createQueryKey('geV2', 'getAllGuidedExperiencesV2', {
      project_id: currentProjectId ?? '',
    }),
    tagValues,
  ];

  const query = useQuery({
    queryKey,
    queryFn:
      currentProjectId && isExperiencesEnabled
        ? async () => {
            const { guided_experiences: experiences } =
              await api.geV2.getAllGuidedExperiencesV2({
                project_id: currentProjectId,
              });

            return experiences;
          }
        : skipToken,
  });

  const experiences =
    isExperiencesEnabled && query.data ? query.data : noExperiences;

  const isLoadingExperiences = query.isLoading;

  const value = useMemo(
    () => ({ experiences, isLoadingExperiences }),
    [experiences, isLoadingExperiences],
  );

  return (
    <ExperienceListContext.Provider value={value}>
      {children}
    </ExperienceListContext.Provider>
  );
};
